'use client';

import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const schema = z
    .object({
        name: z.string().min(2, 'Full name is required'),
        email: z.string().email('Enter a valid email'),
        phone: z.string().optional(),
        password: z
            .string()
            .min(8, 'Password must be at least 8 characters')
            .regex(/[A-Z]/, 'Must contain at least one uppercase letter')
            .regex(/[^a-zA-Z0-9]/, 'Must contain at least one special character'),
        password_confirmation: z.string(),
    })
    .refine((d) => d.password === d.password_confirmation, {
        message: 'Passwords do not match',
        path: ['password_confirmation'],
    });

export type PersonalInfoData = z.infer<typeof schema>;

interface StepPersonalInfoProps {
    defaultValues?: Partial<PersonalInfoData>;
    onNext: (data: PersonalInfoData) => void;
}

export function StepPersonalInfo({ defaultValues, onNext }: StepPersonalInfoProps) {
    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm<PersonalInfoData>({ resolver: zodResolver(schema), defaultValues });

    return (
        <form onSubmit={handleSubmit(onNext)} className="space-y-5">
            {/* Name */}
            <div className="space-y-1.5">
                <Label htmlFor="name" className="text-sm font-medium text-slate-700">
                    Full Name
                </Label>
                <Input
                    id="name"
                    placeholder="Jane Doe"
                    className="h-11 rounded-xl border-slate-200 bg-slate-50 px-3.5 text-sm text-slate-950 placeholder:text-slate-400 focus-visible:bg-white focus-visible:ring-2 focus-visible:ring-violet-500/40"
                    {...register('name')}
                />
                {errors.name && <p className="text-xs text-red-600">{errors.name.message}</p>}
            </div>

            {/* Email */}
            <div className="space-y-1.5">
                <Label htmlFor="email" className="text-sm font-medium text-slate-700">
                    Work Email
                </Label>
                <Input
                    id="email"
                    type="email"
                    placeholder="jane@example.com"
                    className="h-11 rounded-xl border-slate-200 bg-slate-50 px-3.5 text-sm text-slate-950 placeholder:text-slate-400 focus-visible:bg-white focus-visible:ring-2 focus-visible:ring-violet-500/40"
                    {...register('email')}
                />
                {errors.email && <p className="text-xs text-red-600">{errors.email.message}</p>}
            </div>

            {/* Phone */}
            <div className="space-y-1.5">
                <Label htmlFor="phone" className="text-sm font-medium text-slate-700">
                    Phone{' '}
                    <span className="font-normal text-slate-400">(optional)</span>
                </Label>
                <Input
                    id="phone"
                    placeholder="+94 77 123 4567"
                    className="h-11 rounded-xl border-slate-200 bg-slate-50 px-3.5 text-sm text-slate-950 placeholder:text-slate-400 focus-visible:bg-white focus-visible:ring-2 focus-visible:ring-violet-500/40"
                    {...register('phone')}
                />
                <p className="text-xs text-slate-400">Used for account and business contact updates.</p>
            </div>

            {/* Password row */}
            <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                    <Label htmlFor="password" className="text-sm font-medium text-slate-700">
                        Password
                    </Label>
                    <Input
                        id="password"
                        type="password"
                        placeholder="Min. 8 chars"
                        className="h-11 rounded-xl border-slate-200 bg-slate-50 px-3.5 text-sm text-slate-950 placeholder:text-slate-400 focus-visible:bg-white focus-visible:ring-2 focus-visible:ring-violet-500/40"
                        {...register('password')}
                    />
                    {errors.password && <p className="text-xs text-red-600">{errors.password.message}</p>}
                </div>

                <div className="space-y-1.5">
                    <Label htmlFor="password_confirmation" className="text-sm font-medium text-slate-700">
                        Confirm Password
                    </Label>
                    <Input
                        id="password_confirmation"
                        type="password"
                        placeholder="Repeat password"
                        className="h-11 rounded-xl border-slate-200 bg-slate-50 px-3.5 text-sm text-slate-950 placeholder:text-slate-400 focus-visible:bg-white focus-visible:ring-2 focus-visible:ring-violet-500/40"
                        {...register('password_confirmation')}
                    />
                    {errors.password_confirmation && (
                        <p className="text-xs text-red-600">{errors.password_confirmation.message}</p>
                    )}
                </div>
            </div>

            <div className="pt-1">
                <Button
                    type="submit"
                    className="h-11 w-full rounded-xl bg-violet-600 text-sm font-semibold text-white shadow-sm hover:bg-violet-700 focus-visible:ring-2 focus-visible:ring-violet-500/50"
                >
                    Continue
                    <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
            </div>
        </form>
    );
}